import { memo, useCallback } from 'react';

import { useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';
import { useDispatch } from 'react-redux';

import { HandleDismissedType } from 'src/redux/flags/types';
import { scrollTo } from 'src/redux/global/actions/scroll-to';
import { REFRESH_CODE_REVIEW_DATA_FROM_POLL_RESULTS } from 'src/redux/pull-request/actions';
import { PR_UPDATE_FLAG_ID } from 'src/redux/pull-request/sagas/poll-pullrequest-updates-saga';
import { messages } from 'src/sections/repository/sections/pull-request/pull-request.i18n';
import { PullRequestUpdatesResponse } from 'src/types/pull-request';

import { BasePullRequestUpdatedFlag } from './base-pull-request-updated-flag';

type Props = {
  id: typeof PR_UPDATE_FLAG_ID;
  props?: {
    response: PullRequestUpdatesResponse;
  };
} & HandleDismissedType;

function getFlagText(response?: PullRequestUpdatesResponse): {
  title: MessageDescriptor;
  description?: MessageDescriptor;
} {
  if (response) {
    if (response.anchorMoved) {
      return {
        title: messages.anchorMovedTitle,
        description: messages.anchorMovedDescription,
      };
    }
  }
  return {
    title: messages.pullRequestUpdated,
  };
}

export const PullRequestUpdatedFlag = memo((props: Props) => {
  const { id, handleDismissed, ...flagProps } = props;
  const intl = useIntl();
  const dispatch = useDispatch();

  const handleRefresh = useCallback(() => {
    dispatch({ type: REFRESH_CODE_REVIEW_DATA_FROM_POLL_RESULTS });
    dispatch(
      scrollTo({
        targetId: 'root',
      })
    );
  }, [dispatch]);

  const { title, description } = getFlagText(flagProps.props?.response);
  const formattedTitle = title ? intl.formatMessage(title) : '';
  const formattedDescription = description
    ? intl.formatMessage(description)
    : '';

  return (
    <BasePullRequestUpdatedFlag
      id={id}
      title={formattedTitle}
      description={formattedDescription}
      onRefresh={handleRefresh}
      handleDismissed={handleDismissed}
      {...flagProps}
    />
  );
});
